import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { ButtonLoader, FullPageDotsLoader } from "@/clientWeb/components/ui/page-loader"
import {
  BarChart3, Bell, Check, MessageSquareText,
  Paintbrush, Sparkles, UserPlus, ListChecks, ArrowRight,
} from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { defaultWebinarData, type WebinarFormData } from "./steps/types"
import { StepCreateWebinar } from "./steps/StepCreateWebinar"
import { StepRegistration } from "./steps/StepRegistration"
import { StepNotifications } from "./steps/StepNotifications"
import { StepBranding } from "./steps/StepBranding"
import { StepSpeakers } from "./steps/StepSpeakers"
import { StepPolling } from "./steps/StepPolling"
import { StepSurveys } from "./steps/StepSurveys"
import { StepEngagement } from "./steps/StepEngagement"
import { useWebinarStore } from "./store/webinar.store"
import { webinarsService } from "./services/webinar.service"
import {
  isWebinarError,
  isWebinarValidationError,
} from "./services/webinar-error"
import type { StepIndex } from "./utils/webinar.validators"
import { useAuthStore } from "@/features/auth/store/auth.store"

function useSteps() {
  const t = useTranslation()
  return [
    { id: 0, label: t("webinar.createWebinar"), icon: Sparkles, description: t("webinar.stepBasicDetails"), gradient: "linear-gradient(135deg,#fbbf24,#f97316)", iconBg: "bg-gradient-to-br from-amber-400 to-orange-500", dot: "bg-amber-400" },
    { id: 1, label: t("webinar.registration"), icon: UserPlus, description: t("webinar.stepRegistration"), gradient: "linear-gradient(135deg,#38bdf8,#2563eb)", iconBg: "bg-gradient-to-br from-sky-400 to-blue-600", dot: "bg-blue-400" },
    { id: 2, label: t("webinar.notifications"), icon: Bell, description: t("webinar.stepNotifications"), gradient: "linear-gradient(135deg,#a78bfa,#9333ea)", iconBg: "bg-gradient-to-br from-violet-400 to-purple-600", dot: "bg-violet-400" },
    { id: 3, label: t("webinar.branding"), icon: Paintbrush, description: t("webinar.stepBranding"), gradient: "linear-gradient(135deg,#fb7185,#db2777)", iconBg: "bg-gradient-to-br from-rose-400 to-pink-600", dot: "bg-rose-400" },
    { id: 4, label: t("webinar.speakers"), icon: UserPlus, description: t("webinar.stepSpeakers"), gradient: "linear-gradient(135deg,#2dd4bf,#0891b2)", iconBg: "bg-gradient-to-br from-teal-400 to-cyan-600", dot: "bg-teal-400" },
    { id: 5, label: t("webinar.polling"), icon: BarChart3, description: t("webinar.stepPolling"), gradient: "linear-gradient(135deg,#34d399,#16a34a)", iconBg: "bg-gradient-to-br from-emerald-400 to-green-600", dot: "bg-emerald-400" },
    { id: 6, label: t("webinar.surveys"), icon: ListChecks, description: t("webinar.stepSurveys"), gradient: "linear-gradient(135deg,#818cf8,#4f46e5)", iconBg: "bg-gradient-to-br from-indigo-400 to-indigo-600", dot: "bg-indigo-400" },
    { id: 7, label: t("webinar.engagement"), icon: MessageSquareText, description: t("webinar.stepEngagement"), gradient: "linear-gradient(135deg,#fb923c,#ef4444)", iconBg: "bg-gradient-to-br from-orange-400 to-red-500", dot: "bg-orange-400" },
  ]
}

export default function CreateWebinar() {
  const t = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editId = searchParams.get('edit')
  const isEditMode = !!editId

  const refreshWebinars = useWebinarStore((s) => s.refresh)
  const webinars = useWebinarStore((s) => s.webinars)
  const user = useAuthStore((s) => s.user)

  const webinarToEdit = useMemo(
    () => (editId ? webinars.find((w) => w.id === editId) : undefined),
    [editId, webinars]
  )

  const [currentStep, setCurrentStep] = useState(0)
  const [data, setData] = useState<WebinarFormData>(() => {
    const future = new Date(Date.now() + 10 * 60_000)
    const snap = Math.ceil(future.getMinutes() / 5) * 5
    const overflow = snap >= 60
    const h = overflow ? (future.getHours() + 1) % 24 : future.getHours()
    const m = overflow ? 0 : snap
    const defaultTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    const defaultDate = overflow && future.getHours() === 23
      ? new Date(future.getFullYear(), future.getMonth(), future.getDate() + 1)
      : future
    return {
      ...defaultWebinarData,
      date: defaultDate,
      time: defaultTime,
      hostName: user?.name ?? "",
      hostEmail: user?.email ?? "",
    }
  })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const STEPS = useSteps()

  useEffect(() => {
    if (!isEditMode) return
    if (!webinarToEdit) {
      toast.error({ title: t('common.error') ?? 'Error', description: t('webinar.webinarNotFound') })
      navigate('/webinar')
      return
    }
    const d = new Date(webinarToEdit.startTimeMs)
    setData((prev) => ({
      ...prev,
      title: webinarToEdit.title,
      description: webinarToEdit.description,
      date: d,
      time: webinarToEdit.time,
      durationHrs: webinarToEdit.durationHrs,
      durationMins: webinarToEdit.durationMins,
      timezone: webinarToEdit.timezone,
      hostName: webinarToEdit.hostName,
      hostEmail: webinarToEdit.hostEmail,
      maxRegistrants: webinarToEdit.maxRegistrants,
      passwordEnabled: webinarToEdit.passwordEnabled,
      password: webinarToEdit.password,
      recording: webinarToEdit.recording,
      anyTimeJoin: webinarToEdit.anyTimeJoin,
    }))
  }, [isEditMode, webinarToEdit])

  const handleChange = (patch: Partial<WebinarFormData>) =>
    setData((prev) => ({ ...prev, ...patch }))

  const clearError = (key: string) =>
    setErrors((prev) => { const next = { ...prev }; delete next[key]; return next })

  /** Run the service-side validator for the given step. Updates the local
   *  error map and returns `true` when validation passes. */
  const validateStep = (step: StepIndex): boolean => {
    try {
      webinarsService.validateStep(step, data)
      setErrors({})
      return true
    } catch (err) {
      if (isWebinarValidationError(err)) {
        setErrors(err.errors)
        return false
      }
      // Non-validation errors shouldn't happen here, but surface them
      // gracefully if they do.
      toast.error({ title: t('common.error'), description: t('webinar.errors.UNKNOWN') })
      return false
    }
  }

  const persist = async (status: 'upcoming' | 'draft') => {
    if (isEditMode && editId) {
      await webinarsService.update(editId, data, user?.id, status)
    } else {
      await webinarsService.create(data, user?.id, status)
    }
    refreshWebinars()
  }

  const handleCancel = () => {
    navigate("/webinar")
  }

  const handleBack = () => {
    setErrors({})
    setCurrentStep((s) => Math.max(0, s - 1))
  }

  const handleNext = () => {
    if (!validateStep(currentStep as StepIndex)) return
    if (currentStep < STEPS.length - 1) setCurrentStep((s) => s + 1)
  }

  const handleFinish = async () => {
    // Final commit must pass step 0 (basic details) at minimum. We still also
    // validate the current step so users see any per-step errors inline.
    if (!validateStep(0)) {
      setCurrentStep(0)
      return
    }
    if (!validateStep(currentStep as StepIndex)) return
    setSaving(true)
    try {
      await persist('upcoming')
      toast.success({
        title: isEditMode ? t('common.updated') : t('webinar.webinarCreated'),
        description: isEditMode
          ? t('webinar.webinarUpdatedDesc')
          : t('webinar.webinarPublished'),
      })
      navigate('/webinar')
    } catch (err) {
      if (isWebinarValidationError(err)) {
        setErrors(err.errors)
        setCurrentStep(0)
      } else if (isWebinarError(err)) {
        toast.error({
          title: t('webinar.couldNotSaveWebinar'),
          description: t(`webinar.errors.${err.code}`),
        })
      } else {
        toast.error({ title: t('webinar.couldNotSaveWebinar') })
      }
    } finally {
      setSaving(false)
    }
  }

  const handleSkipAndFinish = async () => {
    if (!validateStep(0)) {
      setCurrentStep(0)
      return
    }
    setSaving(true)
    try {
      await persist('upcoming')
      toast.success({
        title: isEditMode ? t('common.updated') : t('webinar.webinarCreated'),
        description: isEditMode
          ? t('webinar.webinarUpdatedDesc')
          : t('webinar.webinarPublished'),
      })
      navigate('/webinar')
    } catch (err) {
      if (isWebinarValidationError(err)) {
        setErrors(err.errors)
        setCurrentStep(0)
      } else if (isWebinarError(err)) {
        toast.error({
          title: t('webinar.couldNotSaveWebinar'),
          description: t(`webinar.errors.${err.code}`),
        })
      } else {
        toast.error({ title: t('webinar.couldNotSaveWebinar') })
      }
    } finally {
      setSaving(false)
    }
  }

  const isLastStep = currentStep === STEPS.length - 1
  const isFirstStep = currentStep === 0
  const progressPct = Math.round((currentStep / (STEPS.length - 1)) * 100)

  const stepComponents: React.ReactNode[] = [
    <StepCreateWebinar key={0} data={data} onChange={handleChange} errors={errors} clearError={clearError} />,
    <StepRegistration key={1} data={data} onChange={handleChange} />,
    <StepNotifications key={2} data={data} onChange={handleChange} />,
    <StepBranding key={3} data={data} onChange={handleChange} />,
    <StepSpeakers key={4} data={data} onChange={handleChange} />,
    <StepPolling key={5} data={data} onChange={handleChange} />,
    <StepSurveys key={6} data={data} onChange={handleChange} />,
    <StepEngagement key={7} data={data} onChange={handleChange} />,
  ]

  const activeStep = STEPS[currentStep]

  return (
    <>
      {saving && <FullPageDotsLoader message={t("webinar.savingWebinar")} />}
      <div className="p-4 sm:p-6 w-full h-full">
        <div className="rounded-3xl w-full border h-full border-border bg-card overflow-hidden shadow-xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 h-full">

            {/* ── Mobile Horizontal Tabs ── */}
            <div className="lg:hidden border-b border-border bg-card">
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <span className="text-muted-foreground text-xs font-semibold">{t("webinar.stepOf", { current: currentStep + 1, total: STEPS.length })}</span>
                  <div className="ml-auto text-xs text-muted-foreground">{progressPct}%</div>
                </div>
                <div className="h-1 rounded-full bg-muted overflow-hidden mb-3">
                  <div
                    className="h-full rounded-full transition-all duration-500 ease-out bg-gradient-to-r from-indigo-500 to-amber-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
                  {STEPS.map((step, idx) => {
                    const Icon = step.icon
                    const isActive = idx === currentStep
                    const isDone = idx < currentStep
                    const isFuture = idx > currentStep
                    return (
                      <button
                        key={step.id}
                        type="button"
                        onClick={() => isDone && setCurrentStep(idx)}
                        disabled={isFuture}
                        className={`shrink-0 flex flex-col items-center gap-1.5 px-3 py-2 rounded-lg transition-all duration-200
                        ${isActive ? "bg-primary/10 border border-primary/20" : ""}
                        ${isDone ? "hover:bg-muted cursor-pointer" : ""}
                        ${isFuture ? "opacity-40 cursor-not-allowed" : ""}
                      `}
                      >
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all
                        ${isActive ? step.iconBg : isDone ? "bg-secondary" : "bg-muted/50"}
                      `}>
                          {isDone
                            ? <Check className="w-3.5 h-3.5 text-white" />
                            : <Icon className={`w-3.5 h-3.5 ${isActive ? "text-white" : "text-muted-foreground"}`} />
                          }
                        </div>
                        <span className={`text-[10px] font-medium leading-tight text-center
                        ${isActive ? "text-foreground" : isDone ? "text-muted-foreground" : "text-muted-foreground/60"}
                      `}>
                          {step.label}
                        </span>
                        {isDone && <div className={`w-1 h-1 rounded-full ${step.dot}`} />}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* ── Left Panel: Enhanced gradient background + vertical stepper ── */}
            <div className="lg:col-span-4 relative overflow-hidden hidden lg:block border-r border-border/50">
              {/* Beautiful gradient background */}
              <div
                className="absolute inset-0"
                style={{
                  background: `
                  linear-gradient(160deg,
                    rgba(12, 90, 150, 0.12) 0%,
                    rgba(26, 123, 196, 0.07) 40%,
                    rgba(176, 219, 255, 0.08) 70%,
                    rgba(12, 90, 150, 0.04) 100%
                  ),
                  radial-gradient(ellipse at 15% 15%, rgba(12, 90, 150, 0.20) 0%, transparent 55%),
                  radial-gradient(ellipse at 85% 85%, rgba(26, 123, 196, 0.12) 0%, transparent 50%),
                  radial-gradient(ellipse at 50% 50%, rgba(176, 219, 255, 0.06) 0%, transparent 60%)
                `
                }}
              />
              {/* Subtle pattern overlay */}
              <div
                className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
                style={{
                  backgroundImage: `
                  radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)
                `,
                  backgroundSize: '20px 20px'
                }}
              />

              {/* Content */}
              <div className="relative z-10 p-6 lg:p-6 h-full flex flex-col">

                {/* Enhanced Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #0C5A96, #1a7bc4)' }}>
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <span className="text-md text-slate-700 dark:text-slate-300 font-bold tracking-widest block">{t("webinar.setupProgress")}</span>
                      <span className="text-slate-500 dark:text-slate-400 text-sm font-medium">{t("webinar.createPerfectWebinar")}</span>
                    </div>
                  </div>

                  {/* Enhanced Progress bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-slate-400 text-sm font-medium">{t("webinar.stepOf", { current: currentStep + 1, total: STEPS.length })}</span>
                      <span className="text-slate-800 dark:text-slate-200 text-xs font-bold">{progressPct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-card/40 dark:bg-slate-800/40 overflow-hidden shadow-inner">
                      <div
                        className="h-full rounded-full transition-all duration-700 ease-out shadow-sm"
                        style={{
                          width: `${progressPct}%`,
                          background: "linear-gradient(90deg, #094776 0%, #0C5A96 40%, #1a7bc4 70%, #77C2F5 100%)",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Enhanced Steps */}
                <nav className="flex flex-col flex-1 gap-1 overflow-y-auto">
                  {STEPS.map((step, idx) => {
                    const Icon = step.icon
                    const isActive = idx === currentStep
                    const isDone = idx < currentStep
                    const isFuture = idx > currentStep
                    const isLast = idx === STEPS.length - 1

                    return (
                      <div key={step.id} className="flex items-stretch gap-4">
                        {/* Left connector column */}
                        <div className="flex flex-col items-center" style={{ width: 36 }}>
                          {/* Enhanced Icon circle */}
                          <div
                            className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center transition-all duration-300 relative
                            ${isActive ? 'shadow-lg' : ''}
                            ${isDone ? 'shadow-md' : ''}
                          `}
                            style={{
                              background: isActive
                                ? step.gradient
                                : isDone
                                  ? 'linear-gradient(135deg, #0C5A96, #1a7bc4)'
                                  : isFuture
                                    ? 'rgba(148, 163, 184, 0.18)'
                                    : 'rgba(100, 116, 139, 0.12)',
                              boxShadow: isActive
                                ? '0 4px 20px rgba(12, 90, 150, 0.35), 0 0 0 3px rgba(12, 90, 150, 0.12)'
                                : isDone
                                  ? '0 2px 10px rgba(12, 90, 150, 0.20)'
                                  : 'none'
                            }}
                          >
                            {isDone
                              ? <Check className="w-4 h-4 text-white" strokeWidth={2.5} />
                              : <Icon className={`w-4 h-4 ${isActive ? "text-white" : isFuture ? "text-slate-400" : "text-slate-600"}`} strokeWidth={isActive ? 2.5 : 2} />
                            }
                          </div>
                          {/* Enhanced Connector line */}
                          {!isLast && (
                            <div className="flex-1 w-0.5 mt-2 mb-2" style={{ minHeight: 16 }}>
                              <div
                                className="w-full h-full rounded-full transition-all duration-500"
                                style={{
                                  background: isDone || isActive
                                    ? 'linear-gradient(to bottom, #0C5A96, #77C2F5)'
                                    : 'rgba(148, 163, 184, 0.25)',
                                }}
                              />
                            </div>
                          )}
                        </div>

                        {/* Enhanced Step card */}
                        <button
                          type="button"
                          onClick={() => isDone && setCurrentStep(idx)}
                          disabled={isFuture}
                          className={`flex-1 flex items-center gap-3 rounded-2xl px-4 py-3 text-left transition-all duration-300 mb-1 relative overflow-hidden
                          ${isActive
                              ? 'bg-card/60 dark:bg-slate-800/40 backdrop-blur-sm border border-white/40 dark:border-slate-700/50 shadow-lg'
                              : isDone
                                ? "bg-card/30 dark:bg-slate-800/20 hover:bg-card/50 dark:hover:bg-slate-800/30 cursor-pointer border border-white/20 dark:border-slate-700/30"
                                : "bg-card/20 dark:bg-slate-800/10 border border-white/10 dark:border-slate-700/20 cursor-not-allowed"
                            }
                          ${isFuture ? "opacity-0.9" : ""}
                        `}
                          style={isActive ? {
                            boxShadow: '0 8px 32px rgba(12, 90, 150, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.25)'
                          } : {}}
                        >
                          {/* Active step gradient overlay */}
                          {isActive && (
                            <div
                              className="absolute inset-0 opacity-10"
                              style={{ background: step.gradient }}
                            />
                          )}
                          <div className="min-w-0 flex-1 relative z-10">
                            <p className={`text-sm font-bold leading-tight transition-colors
                            ${isActive ? "text-slate-900 dark:text-foreground/80 dark:text-white" : isDone ? "text-slate-700 dark:text-slate-300" : "text-slate-500 dark:text-slate-400"}`}>
                              {step.label}
                            </p>
                            <p className={`text-xs mt-1 leading-tight transition-colors
                            ${isActive ? "text-slate-600 dark:text-slate-400" : isDone ? "text-slate-500 dark:text-slate-400" : "text-slate-400 dark:text-slate-500"}`}>
                              {step.description}
                            </p>
                          </div>
                          {isDone && (
                            <div className="shrink-0 w-2 h-2 rounded-full bg-emerald-500 shadow-sm" />
                          )}
                          {isActive && (
                            <ArrowRight className="w-4 h-4 text-slate-600 dark:text-slate-400 shrink-0" strokeWidth={2} />
                          )}
                        </button>
                      </div>
                    )
                  })}
                </nav>
              </div>
            </div>

            {/* ── Right Panel: step content + sticky footer ── */}
            <div className="lg:col-span-8 flex flex-col min-h-0 h-full col-span-1">
              {/* Step header */}
              <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-stroke-100 darK:border-border">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center shadow-md" style={{ background: activeStep.gradient }}>
                    <activeStep.icon className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-white" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-0.5">
                      {t("webinar.stepOf", { current: currentStep + 1, total: STEPS.length })}
                    </p>
                    <h2 className="text-sm sm:text-base font-bold text-foreground leading-tight">
                      {activeStep.label}
                    </h2>
                    <p className="text-xs text-muted-foreground mt-0.5">{activeStep.description}</p>
                  </div>
                </div>
              </div>

              {/* Scrollable step content */}
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-3 sm:py-4 min-h-0">
                {stepComponents[currentStep]}
              </div>

              {/* Sticky footer */}
              <div className="sticky bottom-0 z-20 bg-card/95 backdrop-blur-sm border-t border-border px-4 sm:px-6 py-3 sm:py-4">
                <div className="flex items-center gap-3">
                  {!isFirstStep && (
                    <button
                      type="button"
                      onClick={handleSkipAndFinish}
                      disabled={saving}
                      className="text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors underline underline-offset-2 cursor-pointer"
                    >
                      {t("webinar.skipAndFinish")}
                    </button>
                  )}
                  <div className="flex-1" />
                  {!isFirstStep && (
                    <Button type="button" variant="outline" onClick={handleBack} disabled={saving} className="h-9 sm:h-10 px-4 sm:px-5 text-xs sm:text-sm">
                      {t("common.back")}
                    </Button>
                  )}
                  {isFirstStep && (
                    <Button type="button" variant="outline" onClick={handleCancel} disabled={saving} className="h-9 sm:h-10 px-4 sm:px-5 text-xs sm:text-sm">
                      {t("common.cancel")}
                    </Button>
                  )}
                  {isLastStep ? (
                    <Button type="button" onClick={handleFinish} disabled={saving} className="h-9 sm:h-10 px-5 sm:px-7 min-w-[80px] sm:min-w-[90px] text-xs sm:text-sm">
                      {saving ? <ButtonLoader /> : isEditMode ? (t('common.update') ?? 'Update Webinar') : t("common.finish")}
                    </Button>
                  ) : (
                    <Button type="button" onClick={handleNext} disabled={saving} className="h-9 sm:h-10 px-5 sm:px-7 gap-2 text-xs sm:text-sm">
                      {t("webinar.continue")}
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  )
}
